import { defineStore } from "pinia";
import { computed, ref } from "vue";
import { fetchDfSellableStoreList, type DfSellableStoreListItem } from "@app/api/order/availableStock";

export interface DfSellableStoreSelectOption {
    label: string;
    value: string;
}

/** 侧栏店铺展示名：店铺名-VcCode（与顶部 Tab 文案一致） */
export function formatDfSellableStoreLabel(item: Pick<DfSellableStoreListItem, "storeName" | "vcCode" | "storeId">): string {
    const name = item.storeName?.trim() || item.storeId;
    const vc = item.vcCode?.trim();
    return vc ? `${name}-${vc}` : name;
}

/**
 * 管理端 DF 可售库存店铺列表（会话内缓存）
 * 接口：POST /manage/amazonVcDfSellableStock/storeList
 * 用法对齐 useCorpOptionsStore
 */
export const useDfSellableStoreOptionsStore = defineStore("dfSellableStoreOptions", () => {
    const items = ref<DfSellableStoreListItem[]>([]);
    const loaded = ref(false);
    const loading = ref(false);

    const selectOptions = computed<DfSellableStoreSelectOption[]>(() =>
        items.value.map(item => ({
            label: formatDfSellableStoreLabel(item),
            value: item.storeId,
        })),
    );

    async function fetchOptions(force = false) {
        if (loading.value) return;
        if (loaded.value && !force) return;

        loading.value = true;
        try {
            const data = await fetchDfSellableStoreList();
            items.value = data.items ?? [];
            loaded.value = true;
        } catch (e) {
            console.error("[dfSellableStoreOptions]", e);
            if (force) items.value = [];
        } finally {
            loading.value = false;
        }
    }

    function fetchIfNeeded() {
        return fetchOptions(false);
    }

    function refresh() {
        return fetchOptions(true);
    }

    function reset() {
        items.value = [];
        loaded.value = false;
        loading.value = false;
    }

    function findById(storeId?: string | null) {
        if (!storeId) return undefined;
        return items.value.find(item => item.storeId === String(storeId));
    }

    return { items, loaded, loading, selectOptions, fetchIfNeeded, refresh, reset, findById };
});
